import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { useLike } from '../context/LikeContext'
import { useNavigate } from 'react-router-dom'
import { Search, Filter, Grid, List, Heart, Users, Clock, Edit, Trash2, Share2, Image, Upload, Save, X, MoreVertical, Eye, EyeOff, Box } from 'lucide-react'
import axios from 'axios'

const Container = styled.div`
  padding: 40px;
  background-color: ${props => props.theme.backgroundColor};
  min-height: calc(100vh - 80px);
  width: 100%;

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
  }
`

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`

const HeaderActions = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`

const ViewToggle = styled.div`
  display: flex;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

const ViewButton = styled.button`
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props => props.$active ? props.theme.buttonPrimary : 'transparent'};
  color: ${props => props.$active ? 'white' : props.theme.textSecondary};
  
  &:hover {
    background-color: ${props => props.$active ? props.theme.buttonPrimaryHover : props.theme.hoverBackground};
  }
`

const FilterSection = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
`

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 1px solid ${props => props.theme.border};
  overflow-x: auto;
`

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  background: none;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 3px solid transparent;
  color: ${props => props.$active ? props.theme.buttonPrimary : props.theme.textSecondary};
  font-weight: ${props => props.$active ? '600' : '400'};
  white-space: nowrap;
  
  &:hover {
    color: ${props => props.theme.buttonPrimary};
    background-color: ${props => props.theme.hoverBackground};
  }
  
  ${props => props.$active && `
    border-bottom-color: ${props.theme.buttonPrimary};
    background-color: ${props.theme.hoverBackground};
  `}
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;
  
  @media (max-width: 768px) {
    min-width: 100%;
  }
`

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 14px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }
`

const SearchButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${props => props.theme.textMuted};

  &:hover {
    color: ${props => props.theme.buttonPrimary};
  }
`

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.buttonPrimary};
  }
`

const SetsGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$viewMode === 'grid' ? 'repeat(auto-fill, minmax(340px, 1fr))' : '1fr'};
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const SetCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: ${props => props.$viewMode === 'list' ? 'row' : 'column'};
  align-items: ${props => props.$viewMode === 'list' ? 'center' : 'stretch'};
  gap: ${props => props.$viewMode === 'list' ? '20px' : '16px'};
  min-height: ${props => props.$viewMode === 'list' ? 'auto' : '360px'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 18px;
    min-height: ${props => props.$viewMode === 'list' ? 'auto' : '320px'};
  }
`

const SetImageContainer = styled.div`
  position: relative;
  width: ${props => props.$viewMode === 'list' ? '165px' : '100%'};
  height: ${props => props.$viewMode === 'list' ? '125px' : '200px'};
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: ${props => props.$viewMode === 'list' ? '140px' : '100%'};
    height: ${props => props.$viewMode === 'list' ? '110px' : '160px'};
    margin-bottom: ${props => props.$viewMode === 'list' ? '0' : '12px'};
  }
`

const SetImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${props => props.$image});
  background-size: cover;
  background-position: center;
  border-radius: 8px;
`

const MenuButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${props => props.$isOpen ? '#764ba2' : 'rgba(0, 0, 0, 0.5)'};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  opacity: ${props => props.$isOpen ? '1' : '0'};

  ${SetImageContainer}:hover & {
    opacity: 1;
  }

  &:hover {
    background-color: ${props => props.$isOpen ? '#764ba2' : 'rgba(0, 0, 0, 0.7)'};
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  svg {
    color: white;
  }
`

const DropdownMenu = styled.div`
  position: absolute;
  top: 45px;
  right: 8px;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  z-index: 100;
  overflow: hidden;
  border: 1px solid ${props => props.theme.border};
`

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: ${props => props.$isDanger ? '#d22c2c' : props.theme.textPrimary};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.$isDanger ? 'rgba(220, 38, 38, 0.1)' : props.theme.hoverBackground};
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.border};
  }
`

const SetContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${props => props.$viewMode === 'list' ? '12px' : '16px'};
  height: 100%;
`

const SetTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin: 0;
  line-height: 1.3;
`

const SetDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: ${props => props.$viewMode === 'list' ? 2 : 3};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 13px;
    margin-bottom: 8px;
    -webkit-line-clamp: 2;
  }
`

const SetStats = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 0;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  flex-wrap: wrap;
`

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

// const SetActions = styled.div`
//   display: flex;
//   flex-wrap: wrap;
//   gap: 12px;
//   margin-top: ${props => props.$viewMode === 'list' ? '8px' : 'auto'};
//   padding-top: ${props => props.$viewMode === 'list' ? '0' : '8px'};
//   align-items: stretch;
//   width: 100%;
// `

// const ActionButtonsGrid = styled.div`
//   display: grid;
//   grid-template-columns: repeat(2, minmax(120px, 1fr));
//   gap: 10px;
//   flex: 1 1 auto;

//   @media (max-width: 768px) {
//     grid-template-columns: 1fr;
//   }
// `

// const ActionButton = styled.button`
//   padding: 8px 16px;
//   border: 1px solid ${props => props.theme.border};
//   border-radius: 6px;
//   background-color: transparent;
//   color: ${props => props.theme.textPrimary};
//   cursor: pointer;
//   transition: all 0.2s ease;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   gap: 6px;
//   font-size: 13px;
//   font-weight: 500;
//   line-height: 1.2;
//   white-space: nowrap;
//   letter-spacing: -0.01em;
//   min-width: 120px;
//   text-align: center;

//   &:hover {
//     background-color: ${props => props.theme.hoverBackground};
//     border-color: ${props => props.theme.buttonPrimary};
//     color: ${props => props.theme.buttonPrimary};
//   }

//   &.primary {
//     background-color: ${props => props.theme.buttonPrimary};
//     color: white;
//     border-color: ${props => props.theme.buttonPrimary};

//     &:hover {
//       background-color: ${props => props.theme.buttonPrimaryHover};
//     }
//   }

//   svg {
//     flex-shrink: 0;
//     width: 14px;
//     height: 14px;
//   }

//   @media (max-width: 768px) {
//     padding: 7px 14px;
//     font-size: 12px;
//   }
// `

// const DeleteButton = styled(ActionButton)`
//   border-color: rgba(220, 38, 38, 0.35);
//   color: #d22c2c;
//   flex: 0 0 130px;

//   &:hover {
//     background-color: rgba(220, 38, 38, 0.1);
//     border-color: rgba(220, 38, 38, 0.6);
//     color: #b82323;
//   }
// `

const HeartButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${props => props.theme.textSecondary};
  transition: all 0.2s ease;
  padding: 4px 8px;
  border-radius: 4px;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    color: ${props => props.theme.buttonPrimary};
  }
  
  &.liked {
    color: #ff4757;
  }
  
  &.liked svg {
    fill: #ff4757;
    color: #ff4757;
  }
`

const StatNumber = styled.span`
  font-size: 12px;
  font-weight: 500;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${props => props.theme.textSecondary};
`

const EmptyTitle = styled.h3`
  font-size: 24px;
  margin: 0 0 10px 0;
  color: ${props => props.theme.textPrimary};
`

const EmptyDescription = styled.p`
  font-size: 16px;
  margin: 0 0 30px 0;
  line-height: 1.5;
  color: ${props => props.theme.textSecondary};
`

const CreateButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
`

// 모달 스타일
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`

const ModalContent = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 16px;
  padding: 30px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.textSecondary};
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    color: ${props => props.theme.textPrimary};
  }
`

const CoverImagePreview = styled.div`
  width: 100%;
  height: 300px;
  border-radius: 12px;
  background-image: url(${props => props.$image});
  background-size: cover;
  background-position: center;
  background-color: ${props => props.theme.hoverBackground};
  margin-bottom: 20px;
  border: 2px dashed ${props => props.$hasImage ? 'transparent' : props.theme.border};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`

const PlaceholderText = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 16px;
  text-align: center;
`

const RemoveImageButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #ff4757;
    color: white;
    transform: scale(1.1);
  }
`

const VisibilityBadge = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  backdrop-filter: blur(8px);
  z-index: 2;
  background-color: ${props => props.$isShared ? 'rgba(46, 213, 115, 0.85)' : 'rgba(255, 71, 87, 0.85)'};
  color: white;
`

const UploadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: 2px solid ${props => props.theme.buttonPrimary};
  border-radius: 8px;
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  width: 100%;
  justify-content: center;
  margin-bottom: 15px;

  &:hover {
    background-color: ${props => props.theme.buttonPrimaryHover};
    border-color: ${props => props.theme.buttonPrimaryHover};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`

const InfoText = styled.p`
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
  margin: 0 0 20px 0;
  line-height: 1.5;
`

const DescriptionTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.theme.inputBackground || props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.2s ease;
  margin-bottom: 15px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
  }

  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 10px;
`

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`

const CancelModalButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: transparent;
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    border-color: ${props => props.theme.buttonPrimary};
  }
`

const SaveModalButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`

export default function MySetPage() {
  const { theme } = useTheme()
  const { isLoggedIn, user } = useAuth()
  const { likeStates, initializeLikeStates, toggleLike, isLiked } = useLike()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
  const [activeTab, setActiveTab] = useState('my') // 'my' or 'liked'

  // 내가 만든 문제 세트 데이터
  const [mySets, setMySets] = useState([])

  // 좋아요 한 문제 세트 데이터 (API에서 가져올 데이터)
  const [likedSets, setLikedSets] = useState([])
  const [loadingMy, setLoadingMy] = useState(false)
  const [loadingLiked, setLoadingLiked] = useState(false)
  const [errorMy, setErrorMy] = useState(null)
  const [errorLiked, setErrorLiked] = useState(null)
  const [loading, setLoading] = useState(false)

  // 좋아요 수 상태 관리 (HomePage와 동일한 구조)
  const [likeCounts, setLikeCounts] = useState({})

  // 커버 이미지 수정 모달 관련 상태
  const [showCoverModal, setShowCoverModal] = useState(false)
  const [editingSetId, setEditingSetId] = useState(null)
  const [newCoverImage, setNewCoverImage] = useState('')
  const [originalCoverImage, setOriginalCoverImage] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [originalDescription, setOriginalDescription] = useState('')
  const [uploading, setUploading] = useState(false)

  // 드롭다운 메뉴 상태
  const [openMenuId, setOpenMenuId] = useState(null)
  
  // 내가 만든 퀴즈 목록 조회
  const fetchMyQuizzes = async () => {
    if (!isLoggedIn) return

    try {
      setLoadingMy(true)
      setErrorMy(null)

      console.log('📚 내가 만든 퀴즈 목록 조회 시작')
      console.log('📚 요청 URL:', '/auth/get-my-quizzes')
      console.log('📚 요청 헤더:', { method: 'GET', credentials: 'include' })

      const response = await fetch('/auth/get-my-quizzes', {
        method: 'GET',
        credentials: 'include'
      })

      console.log('📚 응답 상태:', response.status, response.statusText)
      console.log('📚 응답 헤더:', Object.fromEntries(response.headers.entries()))

      if (!response.ok) {
        console.error('📚 HTTP 오류:', response.status, response.statusText)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      console.log('📚 내가 만든 퀴즈 응답:', data)
      
      if (data.success && data.myQuizzes) {
        const formattedMySets = data.myQuizzes.map(quiz => ({
          id: quiz.set_id,
          title: quiz.set_title,
          image: quiz.set_cover_img || '/ThumbnailEx1.jpg',
          set_cover_img: quiz.set_cover_img,  // ★ WaitingRoomPage에서 사용
          likes: parseInt(quiz.set_like_count) || 0,
          plays: parseInt(quiz.set_play_count) || 0,
          questions: quiz.set_obj_count || 0,
          author: quiz.set_author,
          createdAt: quiz.created_at?.split('T')[0] || '',
          status: quiz.status || 'published',
          type: 'my',
          description: quiz.set_description,
          isShared: quiz.set_is_shared !== false
        }))
        
        setMySets(formattedMySets)
        console.log(`✅ 내가 만든 퀴즈 ${formattedMySets.length}개 로드됨`)
      } else {
        console.error('📚 API 응답 실패:', data)
        setErrorMy(data.error || '내가 만든 퀴즈를 불러오는데 실패했습니다.')
      }
    } catch (error) {
      console.error('❌ 내가 만든 퀴즈 목록 조회 실패:', error)
      console.error('❌ 오류 상세:', error.message, error.stack)
      setErrorMy('내가 만든 퀴즈를 불러오는 중 오류가 발생했습니다.')
    } finally {
      setLoadingMy(false)
    }
  }

  // 좋아요한 퀴즈 목록 조회
  const fetchLikedQuizzes = async () => {
    if (!isLoggedIn) return

    try {
      setLoadingLiked(true)
      setErrorLiked(null)

      console.log('💖 좋아요한 퀴즈 목록 조회 시작')
      console.log('💖 요청 URL:', '/auth/get-liked-quizzes')
      console.log('💖 요청 헤더:', { method: 'GET', credentials: 'include' })

      const response = await fetch('/auth/get-liked-quizzes', {
        method: 'GET',
        credentials: 'include'
      })

      console.log('💖 응답 상태:', response.status, response.statusText)
      console.log('💖 응답 헤더:', Object.fromEntries(response.headers.entries()))

      if (!response.ok) {
        console.error('💖 HTTP 오류:', response.status, response.statusText)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      console.log('💖 좋아요한 퀴즈 응답:', data)
      
      if (data.success && data.likedQuizzes) {
        const formattedLikedSets = data.likedQuizzes.map(quiz => ({
          id: quiz.set_id,
          title: quiz.set_title,
          image: quiz.set_cover_img || '/ThumbnailEx1.jpg',
          likes: parseInt(quiz.set_like_count) || 0,
          plays: parseInt(quiz.set_play_count) || 0,
          questions: quiz.set_obj_count || 0,
          author: quiz.set_author,
          createdAt: quiz.created_at?.split('T')[0] || '',
          type: 'liked',
          description: quiz.set_description
        }))
        
        setLikedSets(formattedLikedSets)
        
        // 전역 좋아요 상태 초기화
        initializeLikeStates(formattedLikedSets)
        
        console.log(`✅ 좋아요한 퀴즈 ${formattedLikedSets.length}개 로드됨`)
      } else {
        console.error('💖 API 응답 실패:', data)
        setErrorLiked(data.error || '좋아요한 퀴즈를 불러오는데 실패했습니다.')
      }
    } catch (error) {
      console.error('❌ 좋아요한 퀴즈 목록 조회 실패:', error)
      console.error('❌ 오류 상세:', error.message, error.stack)
      setErrorLiked('좋아요한 퀴즈를 불러오는 중 오류가 발생했습니다.')
    } finally {
      setLoadingLiked(false)
    }
  }
  
  // 컴포넌트 마운트 시 퀴즈 목록 조회
  useEffect(() => {
    if (isLoggedIn) {
      fetchMyQuizzes()
      fetchLikedQuizzes()
    }
  }, [isLoggedIn])

  // 드롭다운 메뉴 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenuId !== null) {
        setOpenMenuId(null)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [openMenuId])

  // 메뉴 토글 함수
  const handleMenuToggle = (e, setId) => {
    e.stopPropagation()
    setOpenMenuId(openMenuId === setId ? null : setId)
  }

  // 현재 활성 탭에 따라 데이터 선택
  const currentSets = activeTab === 'my' ? mySets : likedSets
  
  const filteredSets = currentSets.filter(set =>
    set.title.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleSetAction = (action, setId) => {
    console.log(`${action} 액션 실행:`, setId)
    
    if (action === 'edit') {
      navigate(`/create/edit/${setId}`)
    } else if (action === 'share') {
      // 공유 기능 - 링크 복사
      const shareUrl = `${window.location.origin}/problem/${setId}`
      navigator.clipboard.writeText(shareUrl).then(() => {
        alert('링크가 복사되었습니다!')
      }).catch(() => {
        // 클립보드 API가 지원되지 않는 경우 fallback
        const textArea = document.createElement('textarea')
        textArea.value = shareUrl
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        alert('링크가 복사되었습니다!')
      })
    } else if (action === 'delete') {
      // 삭제 기능 - 확인 알림
      if (window.confirm('정말로 이 문제 세트를 삭제하시겠습니까?')) {
        handleDeleteQuiz(setId)
      }
    }
  }

  // 퀴즈 삭제 함수
  const handleDeleteQuiz = async (setId) => {
    try {
      console.log('🗑️ 퀴즈 삭제 요청:', setId)
      
      const response = await fetch(`/auth/delete-quiz/${setId}`, {
        method: 'DELETE',
        credentials: 'include'
      })
      
      const data = await response.json()
      console.log('🗑️ 퀴즈 삭제 응답:', data)
      
      if (data.success) {
        // 상태에서 해당 문제 세트 제거
        setMySets(prevSets => prevSets.filter(set => set.id !== setId))
        console.log('✅ 퀴즈 삭제됨')
        alert('문제 세트가 삭제되었습니다.')
      } else {
        console.error('❌ 퀴즈 삭제 실패:', data.error)
        alert(data.error || '퀴즈 삭제 중 오류가 발생했습니다.')
      }
    } catch (error) {
      console.error('❌ 퀴즈 삭제 오류:', error)
      alert('퀴즈 삭제 중 오류가 발생했습니다.')
    }
  }

  // 퀴즈 공개/비공개 토글 함수
  const handleToggleVisibility = async (setId) => {
    try {
      const response = await fetch(`/auth/toggle-quiz-visibility/${setId}`, {
        method: 'PUT',
        credentials: 'include'
      })

      const data = await response.json()

      if (data.success) {
        setMySets(prevSets =>
          prevSets.map(set =>
            set.id === setId ? { ...set, isShared: data.isShared } : set
          )
        )
        alert(data.message)
      } else {
        alert(data.error || '공개 상태 변경에 실패했습니다.')
      }
    } catch (error) {
      console.error('❌ 공개 상태 변경 오류:', error)
      alert('공개 상태 변경 중 오류가 발생했습니다.')
    }
  }

  // 좋아요 수 가져오기 함수 (HomePage와 동일)
  const getLikeCount = (setId, originalLikes) => {
    return likeCounts[setId] !== undefined ? likeCounts[setId] : originalLikes
  }

  // const renderActionButtons = (currentSet) => {
  //   if (activeTab === 'my') {
  //     return (
  //       <>
  //         <ActionButtonsGrid>
  //           <ActionButton
  //             onClick={(e) => {
  //               e.stopPropagation()
  //               navigate('/waiting-room', { state: { problemSet: currentSet } })
  //             }}
  //           >
  //             <Users size={12} />
  //             방 만들기
  //           </ActionButton>
  //           <ActionButton
  //             onClick={(e) => {
  //               e.stopPropagation()
  //               handleOpenCoverModal(currentSet.id)
  //             }}
  //           >
  //             <Image size={12} />
  //             커버 수정
  //           </ActionButton>
  //           <ActionButton
  //             onClick={(e) => {
  //               e.stopPropagation()
  //               handleSetAction('edit', currentSet.id)
  //             }}
  //           >
  //             <Edit size={12} />
  //             편집
  //           </ActionButton>
  //           <ActionButton
  //             onClick={(e) => {
  //               e.stopPropagation()
  //               handleSetAction('share', currentSet.id)
  //             }}
  //           >
  //             <Share2 size={12} />
  //             공유
  //           </ActionButton>
  //         </ActionButtonsGrid>
  //         <DeleteButton
  //           onClick={(e) => {
  //             e.stopPropagation()
  //             handleSetAction('delete', currentSet.id)
  //           }}
  //         >
  //           <Trash2 size={12} />
  //           삭제
  //         </DeleteButton>
  //       </>
  //     )
  //   }

  //   return (
  //     <ActionButton
  //       onClick={(e) => {
  //         e.stopPropagation()
  //         handleSetAction('share', currentSet.id)
  //       }}
  //     >
  //       <Share2 size={12} />
  //       공유
  //     </ActionButton>
  //   )
  // }

  // 좋아요 토글 함수 (전역 상태 사용)
  const handleToggleLike = async (setId) => {
    const result = await toggleLike(setId)
    
    if (result.success) {
      const isNowLiked = result.isLiked
      
      // 좋아요 수 업데이트 (HomePage와 동일한 로직)
      setLikeCounts(prev => ({
        ...prev,
        [setId]: (prev[setId] !== undefined ? prev[setId] : 
                 [...mySets, ...likedSets].find(q => q.id === setId)?.likes || 0) + (result.isLiked ? 1 : -1)
      }))
      
      if (isNowLiked) {
        // 좋아요 했다면, 내 퀴즈에서 해당 퀴즈 정보를 찾아서 좋아요 목록에 추가
        const myQuiz = mySets.find(quiz => quiz.id === setId)
        if (myQuiz) {
          const likedQuiz = {
            ...myQuiz,
            type: 'liked'
          }
          setLikedSets(prev => [likedQuiz, ...prev])
        }
        console.log('✅ 좋아요 추가됨')
      } else {
        // 좋아요 취소했다면 좋아요 목록에서 제거
        setLikedSets(prev => prev.filter(quiz => quiz.id !== setId))
        console.log('✅ 좋아요 취소됨')
      }
    } else {
      alert(result.error)
    }
  }

  // 좋아요 취소 함수 (좋아요 한 작품 탭에서 사용) - handleToggleLike를 재사용
  const handleUnlikeQuiz = (setId) => {
    handleToggleLike(setId)
  }

  const handleCreateNew = () => {
    navigate('/create')
  }

  // file.skynet.re.kr로 파일 업로드 함수
  const uploadToSkynet = async (file) => {
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const formData = new FormData()
    formData.append('file', file)
    formData.append('uploadId', uploadId)

    try {
      const response = await axios.post('/ObjectUpload/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      const uploadedUrl = response.data?.data?.data?.files?.[0]?.url

      if (uploadedUrl) {
        console.log('✅ 파일 업로드 성공:', uploadedUrl)
        return uploadedUrl
      } else {
        throw new Error('업로드된 URL을 받지 못했습니다.')
      }
    } catch (error) {
      console.error('❌ 파일 업로드 실패:', error)
      throw error
    }
  }

  // 커버 수정 모달 열기
  const handleOpenCoverModal = (setId) => {
    const currentSet = mySets.find(set => set.id === setId)
    if (currentSet) {
      setEditingSetId(setId)
      setNewCoverImage(currentSet.image || '')
      setOriginalCoverImage(currentSet.image || '')
      setNewDescription(currentSet.description || '')
      setOriginalDescription(currentSet.description || '')
      setShowCoverModal(true)
    }
  }

  // 커버 수정 모달 닫기
  const hasModalChanges = newCoverImage !== originalCoverImage || newDescription !== originalDescription
  const handleCloseCoverModal = () => {
    if (hasModalChanges) {
      if (window.confirm('변경사항이 저장되지 않았습니다. 정말 닫으시겠습니까?')) {
        setShowCoverModal(false)
        setEditingSetId(null)
        setNewCoverImage('')
        setOriginalCoverImage('')
        setNewDescription('')
        setOriginalDescription('')
      }
    } else {
      setShowCoverModal(false)
      setEditingSetId(null)
      setNewCoverImage('')
      setOriginalCoverImage('')
      setNewDescription('')
      setOriginalDescription('')
    }
  }

  // 이미지 업로드 처리
  const handleImageUpload = async () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'

    input.onchange = async (e) => {
      const file = e.target.files[0]
      if (file) {
        const maxSize = 5 * 1024 * 1024
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2)

        if (file.size > maxSize) {
          alert(`이미지 파일이 너무 큽니다.\n현재 크기: ${fileSizeMB}MB\n최대 허용 크기: 5MB\n\n파일 크기를 줄여서 다시 업로드해주세요.`)
          return
        }

        console.log(`📷 커버 이미지 업로드 시작: ${file.name} (${fileSizeMB}MB)`)

        try {
          setUploading(true)
          const uploadedUrl = await uploadToSkynet(file)
          setNewCoverImage(uploadedUrl)
          console.log(`✅ 커버 이미지 업로드 완료: ${uploadedUrl}`)
        } catch (error) {
          alert(`이미지 업로드에 실패했습니다: ${error.message}`)
          console.error('이미지 업로드 오류:', error)
        } finally {
          setUploading(false)
        }
      }
    }

    input.click()
  }

  // 이미지 제거
  const handleRemoveImage = () => {
    setNewCoverImage('')
  }

  // 커버 이미지 저장
  const handleSaveCoverImage = async () => {
    if (!hasModalChanges) {
      alert('변경된 내용이 없습니다.')
      return
    }

    try {
      setLoading(true)
      console.log('💾 커버 이미지/설명 저장 시작:', { id: editingSetId, coverImage: newCoverImage, description: newDescription })

      const response = await fetch(`/auth/update-quiz-cover/${editingSetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ coverImage: newCoverImage, description: newDescription })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      console.log('💾 커버 이미지/설명 저장 응답:', data)

      if (data.success) {
        // 로컬 상태 업데이트
        setMySets(prevSets =>
          prevSets.map(set =>
            set.id === editingSetId ? { ...set, image: newCoverImage, description: newDescription } : set
          )
        )

        alert('변경사항이 저장되었습니다!')
        setShowCoverModal(false)
        setEditingSetId(null)
        setNewCoverImage('')
        setOriginalCoverImage('')
        setNewDescription('')
        setOriginalDescription('')
      } else {
        throw new Error(data.error || '저장에 실패했습니다.')
      }
    } catch (error) {
      console.error('❌ 커버 이미지 저장 실패:', error)
      alert('커버 이미지 저장 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  if (!isLoggedIn) {
    return (
      <Container theme={theme}>
        <EmptyState theme={theme}>
          <EmptyTitle theme={theme}>로그인이 필요합니다</EmptyTitle>
          <EmptyDescription theme={theme}>
            내 문제 세트를 보려면 먼저 로그인해주세요.
          </EmptyDescription>
          <CreateButton onClick={() => navigate('/login')}>
            로그인하기
          </CreateButton>
        </EmptyState>
      </Container>
    )
  }

  return (
    <Container theme={theme}>
      <Header>
        <Title theme={theme}>보관함</Title>
        <HeaderActions>
          <ViewToggle theme={theme}>
            <ViewButton 
              theme={theme} 
              $active={viewMode === 'grid'} 
              onClick={() => setViewMode('grid')}
            >
              <Grid size={16} />
            </ViewButton>
            <ViewButton 
              theme={theme} 
              $active={viewMode === 'list'} 
              onClick={() => setViewMode('list')}
            >
              <List size={16} />
            </ViewButton>
          </ViewToggle>
          <CreateButton onClick={handleCreateNew}>
            새로 만들기
          </CreateButton>
        </HeaderActions>
      </Header>

      <TabsContainer>
        <Tab 
          theme={theme}
          $active={activeTab === 'my'}
          onClick={() => setActiveTab('my')}
        >
          <Edit size={16} />
          내 작품
        </Tab>
        <Tab
          theme={theme}
          $active={activeTab === 'liked'}
          onClick={() => setActiveTab('liked')}
        >
          <Heart size={16} />
          좋아요 한 작품
        </Tab>
      </TabsContainer>

      <FilterSection>
        <SearchBar>
          <SearchInput
            theme={theme}
            type="text"
            placeholder={activeTab === 'my' ? "내 문제 세트 검색..." : "좋아요 한 문제 세트 검색..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchButton theme={theme}>
            <Search size={16} />
          </SearchButton>
        </SearchBar>
        <FilterButton theme={theme}>
          <Filter size={16} />
          필터
        </FilterButton>
      </FilterSection>

      {(activeTab === 'my' ? loadingMy : loadingLiked) ? (
        <EmptyState theme={theme}>
          <EmptyTitle theme={theme}>
            {activeTab === 'my' ? '내 문제 세트를 불러오는 중...' : '좋아요한 문제 세트를 불러오는 중...'}
          </EmptyTitle>
        </EmptyState>
      ) : (activeTab === 'my' ? errorMy : errorLiked) ? (
        <EmptyState theme={theme}>
          <EmptyTitle theme={theme}>
            오류가 발생했습니다
          </EmptyTitle>
          <EmptyDescription theme={theme}>
            {activeTab === 'my' ? errorMy : errorLiked}
          </EmptyDescription>
        </EmptyState>
      ) : filteredSets.length === 0 ? (
        <EmptyState theme={theme}>
          <EmptyTitle theme={theme}>
            {activeTab === 'my' ? '아직 만든 문제 세트가 없습니다' : '아직 좋아요 한 문제 세트가 없습니다'}
          </EmptyTitle>
          <EmptyDescription theme={theme}>
            {activeTab === 'my'
              ? '첫 번째 문제 세트를 만들어 친구들과 공유해보세요!'
              : '마음에 드는 문제 세트에 좋아요를 눌러보세요!'
            }
          </EmptyDescription>
          {activeTab === 'my' && (
            <CreateButton onClick={handleCreateNew}>
              지금 만들기
            </CreateButton>
          )}
        </EmptyState>
      ) : (
        <SetsGrid $viewMode={viewMode}>
          {filteredSets.map(set => (
            <SetCard
              key={set.id}
              theme={theme}
              $viewMode={viewMode}
              onClick={() => navigate(`/problem/${set.id}`)}
              style={{ cursor: 'pointer' }}
            >
              <SetImageContainer $viewMode={viewMode}>
                <SetImage $image={set.image} />
                <MenuButton
                  onClick={(e) => handleMenuToggle(e, set.id)}
                  $isOpen={openMenuId === set.id}
                >
                  <MoreVertical size={22} />
                </MenuButton>
                {activeTab === 'my' && (
                  <VisibilityBadge $isShared={set.isShared}>
                    {set.isShared ? <><Eye size={12} /> 공개</> : <><EyeOff size={12} /> 비공개</>}
                  </VisibilityBadge>
                )}
                {openMenuId === set.id && (
                  <DropdownMenu theme={theme} onClick={(e) => e.stopPropagation()}>
                    {activeTab === 'my' && (
                      <DropdownItem
                        theme={theme}
                        onClick={(e) => {
                          e.stopPropagation()
                          setOpenMenuId(null)
                          handleToggleVisibility(set.id)
                        }}
                      >
                        {set.isShared ? <><EyeOff size={16} /> 비공개로 전환</> : <><Eye size={16} /> 공개로 전환</>}
                      </DropdownItem>
                    )}
                    <DropdownItem
                      theme={theme}
                      onClick={(e) => {
                        e.stopPropagation()
                        setOpenMenuId(null)
                        // ★★★ 테스트방 생성: 작성자가 자신의 퀴즈를 테스트하는 방
                        navigate('/waiting-room', {
                          state: {
                            problemSet: set,
                            roomType: 'test',  // 테스트방
                            progressMode: 'sequential',  // 순서 모드 (강제)
                            endCondition: 'none',  // 종료 조건 없음 (강제)
                            hostUserId: user?.username || set.author  // 작성자 정보
                          }
                        })
                      }}
                    >
                      <Users size={16} />
                      테스트 플레이
                    </DropdownItem>
                    <DropdownItem
                      theme={theme}
                      onClick={(e) => {
                        e.stopPropagation()
                        setOpenMenuId(null)
                        handleOpenCoverModal(set.id)
                      }}
                    >
                      <Image size={16} />
                      커버 수정
                    </DropdownItem>
                    <DropdownItem
                      theme={theme}
                      onClick={(e) => {
                        e.stopPropagation()
                        setOpenMenuId(null)
                        handleSetAction('edit', set.id)
                      }}
                    >
                      <Edit size={16} />
                      편집
                    </DropdownItem>
                    <DropdownItem
                      theme={theme}
                      onClick={(e) => {
                        e.stopPropagation()
                        setOpenMenuId(null)
                        handleSetAction('share', set.id)
                      }}
                    >
                      <Share2 size={16} />
                      공유
                    </DropdownItem>
                    <DropdownItem
                      theme={theme}
                      $isDanger={true}
                      onClick={(e) => {
                        e.stopPropagation()
                        setOpenMenuId(null)
                        handleSetAction('delete', set.id)
                      }}
                    >
                      <Trash2 size={16} />
                      삭제
                    </DropdownItem>
                  </DropdownMenu>
                )}
              </SetImageContainer>
              <SetContent $viewMode={viewMode}>
                <SetTitle theme={theme}>{set.title}</SetTitle>
                {set.description && (
                  <SetDescription theme={theme} $viewMode={viewMode}>
                    {set.description}
                  </SetDescription>
                )}
                <SetStats theme={theme} $viewMode={viewMode}>
                  <HeartButton
                    theme={theme}
                    className={isLiked(set.id) ? 'liked' : ''}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleToggleLike(set.id)
                    }}
                  >
                    <Heart size={12} />
                    <StatNumber>{getLikeCount(set.id, set.likes)}</StatNumber>
                  </HeartButton>
                  <StatItem>
                    <Users size={12} />
                    {set.plays}번 플레이
                  </StatItem>
                  <StatItem>
                    <Clock size={12} />
                    물체 {set.questions}개
                  </StatItem>
                  {set.author && (
                    <StatItem>
                      <Users size={12} />
                      {set.author}
                    </StatItem>
                  )}
                </SetStats>
              </SetContent>
            </SetCard>
          ))}
        </SetsGrid>
      )}

      {/* 커버 이미지 수정 모달 */}
      {showCoverModal && (
        <ModalOverlay onClick={handleCloseCoverModal}>
          <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle theme={theme}>커버 이미지 수정</ModalTitle>
              <CloseButton theme={theme} onClick={handleCloseCoverModal}>
                <X size={24} />
              </CloseButton>
            </ModalHeader>

            <CoverImagePreview
              theme={theme}
              $image={newCoverImage}
              $hasImage={!!newCoverImage}
            >
              {!newCoverImage && (
                <PlaceholderText theme={theme}>
                  커버 이미지를 업로드해주세요
                </PlaceholderText>
              )}
              {newCoverImage && (
                <RemoveImageButton onClick={handleRemoveImage}>
                  <X size={20} />
                </RemoveImageButton>
              )}
            </CoverImagePreview>

            <UploadButton
              theme={theme}
              onClick={handleImageUpload}
              disabled={uploading}
            >
              <Upload size={16} />
              {uploading ? '업로드 중...' : '이미지 업로드'}
            </UploadButton>

            <SectionLabel theme={theme}>퀴즈 세트 설명</SectionLabel>
            <DescriptionTextarea
              theme={theme}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="퀴즈 세트에 대한 설명을 입력해주세요..."
              maxLength={500}
            />

            <ModalButtonGroup>
              <CancelModalButton theme={theme} onClick={handleCloseCoverModal}>
                취소
              </CancelModalButton>
              <SaveModalButton
                theme={theme}
                onClick={handleSaveCoverImage}
                disabled={loading || uploading || !hasModalChanges}
              >
                <Save size={16} />
                저장
              </SaveModalButton>
            </ModalButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  )
}
